<template>
  <section class="buy-build" :class="{ 'buy-build--compact': compact }" aria-labelledby="buy-build-title">
    <header class="buy-build__head">
      <div>
        <div class="buy-build__kicker">{{ panelKicker }}<template v-if="compact"> · {{ plan.selectedCount }} parts</template></div>
        <h3 id="buy-build-title">{{ heading }}</h3>
      </div>
      <div v-if="compact" class="buy-build__estimate"><span>Estimated total</span><strong>{{ totalDisplay || 'Check Amazon' }}</strong></div>
      <span v-else class="buy-build__count">{{ plan.selectedCount }} part{{ plan.selectedCount == 1 ? '' : 's' }}</span>
    </header>

    <p v-if="compact && !issues.length" class="buy-build__compat-summary" :class="'tone-' + compatTone"><span class="icon" :class="conflicts || warnings ? 'icon-warning' : 'icon-check'" aria-hidden="true"></span> {{ compatValue }}</p>
    <component :is="compact ? 'details' : 'div'" class="buy-build__analysis">
    <summary v-if="compact">Performance &amp; power</summary>
    <div class="buy-build__metrics" :class="{ 'buy-build__metrics--five': !!balanceValue && !compact, 'buy-build__metrics--compact': compact }">
      <div v-if="!compact" class="buy-build__metric" :class="'buy-build__metric--' + compatTone">
        <span>Compatibility</span>
        <strong>{{ compatValue }}</strong>
      </div>
      <div class="buy-build__metric" :class="'buy-build__metric--' + powerTone">
        <span>Power headroom</span>
        <strong>{{ powerValue }}</strong>
      </div>
      <div v-if="balanceValue" class="buy-build__metric" :title="balanceDetail">
        <span>CPU / GPU balance</span>
        <strong>{{ balanceValue }}</strong>
      </div>
      <div class="buy-build__metric">
        <span>Performance tier</span>
        <strong :class="tier.className">{{ tier.label }}</strong>
      </div>
      <div v-if="!compact" class="buy-build__metric">
        <span>Estimated total</span>
        <strong class="buy-build__total">{{ totalDisplay || 'Check Amazon' }}</strong>
      </div>
    </div>
    </component>

    <!-- Compatibility findings are advisory: our catalog data is imperfect, so we
         state the problem plainly and still let the builder decide. The metric
         above already carries the count, so this lists the findings themselves. -->
    <div v-if="issues.length" class="buy-build__alert" :class="['buy-build__alert--' + compatTone, { 'buy-build__alert--fit-note': compact && fitNotesOnly }]" role="status">
      <span class="icon" :class="compact && fitNotesOnly ? 'icon-info-with-circle' : compatTone == 'danger' ? 'icon-circle-with-cross' : 'icon-warning'" aria-hidden="true"></span>
      <div class="buy-build__alert-body">
        <div v-for="(issue, index) in issues" :key="index" class="buy-build__finding">
          <p><span v-if="issues.length > 1" class="buy-build__finding-mark" aria-hidden="true">&bull;</span>{{ issue.text }}</p>
          <!-- Naming the two parts turns "RAM speed mismatch" into something the
               reader can act on: each one opens in its category slider. -->
          <p v-if="issue.parts.length && (!compact || issuesExpanded)" class="buy-build__finding-parts">
            <router-link
              v-for="part in issue.parts"
              :key="part.key"
              class="buy-build__finding-part"
              :to="part.to"
            >
              <span class="buy-build__finding-part-kind">{{ part.category }}</span>
              <span class="buy-build__finding-part-name">{{ part.label }}</span>
            </router-link>
          </p>
        </div>
        <button v-if="compact && issues.some(issue => issue.parts.length)" type="button" class="buy-build__details-toggle" :aria-expanded="issuesExpanded ? 'true' : 'false'" @click="issuesExpanded = !issuesExpanded">{{ issuesExpanded ? 'Hide affected parts' : 'View affected parts' }} <span aria-hidden="true">{{ issuesExpanded ? '−' : '+' }}</span></button>
      </div>
    </div>

    <div class="buy-build__cta" :class="{ 'buy-build__cta--bulk': plan.mode == 'bulk' && plan.url }">
      <!-- Above the button, not after it: this changes what the button does. -->
      <p v-if="excludedNotice" class="buy-build__excluded">
        <span class="icon icon-info-with-circle"></span>
        {{ excludedNotice }}.
      </p>

      <a
        v-if="plan.mode == 'bulk' && plan.url"
        class="btn btn-primary buy-build__primary"
        :href="plan.url"
        target="_blank"
        rel="nofollow sponsored noopener noreferrer"
        :aria-label="cartActionLabel + ' (opens in a new tab)'"
        @click="onCartClick"
      >
        <span class="icon icon-shopping-cart"></span>
        <span>{{ cartActionLabel }}</span>
      </a>

      <p v-if="plan.mode == 'bulk' && plan.url" class="buy-build__cta-note buy-build__cta-note--handoff">
        Opens Amazon in a new tab. Review prices, sellers and shipping before checkout.
      </p>

      <!-- Sequential fallback: one cart link per part, rendered as explicit steps.
           Firing several windows from one click would be blocked anyway. -->
      <div v-else-if="plan.mode == 'sequential' && plan.steps.length" class="buy-build__steps">
        <p class="buy-build__cta-note">Add each part to your Amazon cart:</p>
        <ol>
          <li v-for="(step, index) in plan.steps" :key="step.row.key">
            <a
              :href="step.url"
              target="_blank"
              rel="nofollow sponsored noopener noreferrer"
              @click="onStepClick(step, index)"
            >{{ step.row.label }} — {{ step.row.name }}</a>
            <span v-if="step.row.qty > 1">×{{ step.row.qty }}</span>
          </li>
        </ol>
      </div>

      <p v-else class="buy-build__cta-note buy-build__cta-note--empty">
        None of the selected parts have an Amazon listing yet, so there is nothing to add to a cart.
      </p>

      <!-- Surface-specific secondary action (e.g. "Save build" in the builder).
           Vue 2.6 compiles <template #secondary> into $scopedSlots, so check both. -->
      <div v-if="$scopedSlots.secondary || $slots.secondary" class="buy-build__secondary">
        <slot name="secondary"></slot>
      </div>
    </div>

    <!-- The builder already lists every part in the checklist beside this panel,
         so repeating them here only made the card tall. The saved-build library
         has no checklist, so it keeps the table. -->
    <div v-if="showParts" class="buy-build__parts">
      <div class="buy-build__parts-head">
        <span>Part</span>
        <span>Selection</span>
        <span>{{ showPrices ? 'Price' : 'Estimated' }}</span>
        <span>Amazon</span>
      </div>
      <div
        v-for="row in rows"
        :key="row.key"
        class="buy-build__row"
        :class="{ 'is-missing': !row.item, 'is-unavailable': row.unavailable, 'is-excluded': row.item && !row.asin }"
      >
        <div class="buy-build__row-label">
          <span class="icon" :class="row.icon"></span>
          <span>{{ row.label }}</span>
          <span v-if="row.qty > 1" class="buy-build__qty">×{{ row.qty }}</span>
        </div>
        <div class="buy-build__row-name">
          <strong>{{ row.name }}</strong>
          <span v-if="row.unavailable" class="buy-build__row-flag">
            <span class="icon icon-warning"></span> Unavailable on Amazon
          </span>
          <span v-else-if="row.item && !row.asin" class="buy-build__row-flag">
            <span class="icon icon-warning"></span> No Amazon listing — not added to the cart
          </span>
          <span v-else-if="row.summary">{{ row.summary }}</span>
        </div>
        <div class="buy-build__row-price">
          <template v-if="row.hasPrice && showPrices">
            <strong>${{ row.price }}</strong>
            <span v-if="row.qty > 1">{{ row.qty }} × ${{ row.unitPrice }}</span>
          </template>
          <span v-else-if="row.hasPrice" class="buy-build__row-band">{{ row.priceBand }}</span>
          <span v-else>Check Amazon</span>
        </div>
        <div class="buy-build__row-link">
          <a
            v-if="row.amazonUrl"
            :href="row.amazonUrl"
            target="_blank"
            rel="nofollow sponsored noopener noreferrer"
            @click="onPartClick(row)"
          >Amazon</a>
          <span v-else>No ASIN</span>
        </div>
      </div>
    </div>

    <p class="buy-build__disclosure">
      GixCore saves a parts list, not a prebuilt PC. As an Amazon Associate I earn from qualifying purchases.
    </p>
  </section>
</template>

<script>
import { SHOW_PRICES } from '../../config'
import { functions } from '../../functions'
import { cartPlan, excludedPartsNotice } from '../../utils/amazonCart'
import { performanceTier } from '../../utils/buildParts'
import { formatCompatMessage } from '../../utils/compatMessage'
import analytics from '../../services/analytics'

export default {
  name: 'BuyBuildPanel',
  props: {
    compact: {
      type: Boolean,
      default: false
    },
    // Normalized rows from utils/buildParts (either build shape produces these).
    rows: {
      type: Array,
      default: () => []
    },
    // { conflicts, warnings, issues: [] } from whichever surface owns the checks.
    compatibility: {
      type: Object,
      default: () => ({})
    },
    // { watts, wattage } -- estimated draw and the selected PSU's rating.
    power: {
      type: Object,
      default: () => ({})
    },
    // { value, detail } -- CPU/GPU balance, supplied by the builder only. Absent
    // on the saved-build surface, where the panel falls back to four metrics.
    balance: {
      type: Object,
      default: () => ({})
    },
    // 0-100 build performance score.
    performance: {
      type: Number,
      default: 0
    },
    total: {
      type: Number,
      default: 0
    },
    // Identifies the trigger surface in analytics: builder_completion | my_builds.
    source: {
      type: String,
      default: 'builder_completion'
    },
    buildId: {
      type: [String, Number],
      default: null
    },
    buildName: {
      type: String,
      default: ''
    },
    // The per-part table. Off in the builder, where the checklist already shows
    // the same parts a few hundred pixels to the left.
    showParts: {
      type: Boolean,
      default: true
    },
    // Only the surface where completion actually happens reports it. The saved
    // build library renders builds that were completed some time ago, so leaving
    // this off keeps one library visit from logging a completion per card.
    reportCompletion: {
      type: Boolean,
      default: false
    }
  },
  data() {
    return {
      completedSignature: '',
      issuesExpanded: false
    }
  },
  computed: {
    showPrices(){
      return SHOW_PRICES
    },
    plan(){
      return cartPlan(this.rows)
    },
    excludedNotice(){
      // "listed below" is only true when the table is rendered.
      return excludedPartsNotice(this.plan, { listed: this.showParts })
    },
    heading(){
      if(this.compact && (this.conflicts || this.warnings || this.plan.excludedCount)){ return 'Review your build' }
      return this.buildName || 'Your parts list is ready'
    },
    panelKicker(){
      if(this.conflicts){ return 'Parts list complete · action needed' }
      return 'Parts list complete'
    },
    tier(){
      return performanceTier(this.performance)
    },
    conflicts(){
      return Number(this.compatibility && this.compatibility.conflicts) || 0
    },
    warnings(){
      return Number(this.compatibility && this.compatibility.warnings) || 0
    },
    issues(){
      var list = (this.compatibility && this.compatibility.issues) || []
      // Two shapes arrive here: the builder sends findings with the parts they
      // were checked across, a saved build sends bare messages. Store messages
      // are shouted fragments with embedded newlines, so they are normalised on
      // the way in either way.
      return list.map((issue) => {
        var source = issue && typeof issue == 'object' ? issue : { text: issue }
        var text = this.friendlyCompatMessage(formatCompatMessage(source.text))
        return text ? { text: text, parts: source.parts || [] } : null
      }).filter(Boolean)
    },
    fitNotesOnly(){
      // Only the explicit compatible-fit advisory gets the quiet treatment.
      // Mixed findings and conflicts retain the warning/attention styling.
      return !this.conflicts && this.issues.length > 0
        && this.issues.every((issue) => /^Compatible fit:/i.test(issue.text))
    },
    balanceValue(){
      return (this.balance && this.balance.value) || ''
    },
    balanceDetail(){
      return (this.balance && this.balance.detail) || ''
    },
    // "6 listings (6 items)" restated the same number twice. The distinction only
    // matters when a listing is being bought more than once.
    cartContentsLabel(){
      var lines = this.plan.lineCount
      var units = this.plan.unitCount
      var listings = lines + ' listing' + (lines === 1 ? '' : 's')
      if(units === lines){
        return lines + ' item' + (lines === 1 ? '' : 's')
      }
      return listings + ' (' + units + ' item' + (units === 1 ? '' : 's') + ')'
    },
    cartActionLabel(){
      return 'Review ' + this.cartContentsLabel + ' on Amazon'
    },
    compatTone(){
      if(this.conflicts){ return 'danger' }
      if(this.warnings){ return 'warning' }
      return 'good'
    },
    compatValue(){
      if(this.conflicts){
        return this.conflicts + (this.conflicts === 1 ? ' conflict' : ' conflicts')
      }
      if(this.warnings){
        return 'Compatible — ' + this.warnings + (this.warnings === 1 ? ' warning' : ' warnings') + ' to review'
      }
      return 'No conflicts'
    },
    estimatedPower(){
      var watts = parseInt(this.power && this.power.watts)
      return isNaN(watts) ? 0 : Math.max(0, watts)
    },
    psuWattage(){
      var wattage = parseInt(this.power && this.power.wattage)
      return isNaN(wattage) || wattage <= 0 ? null : wattage
    },
    powerTone(){
      if(!this.psuWattage || !this.estimatedPower){ return 'idle' }
      var ratio = this.estimatedPower / this.psuWattage
      if(ratio > 1){ return 'danger' }
      if(ratio > 0.8){ return 'warning' }
      return 'good'
    },
    powerValue(){
      if(!this.estimatedPower){ return 'Not estimated' }
      if(!this.psuWattage){ return this.estimatedPower + ' W draw' }
      if(this.estimatedPower > this.psuWattage){
        return (this.estimatedPower - this.psuWattage) + ' W over'
      }
      // "headroom" everywhere; the status panel and this metric used to give the
      // same number two different names.
      return (this.psuWattage - this.estimatedPower) + ' W headroom'
    },
    totalValue(){
      var value = functions.parsePrice(this.total)
      return value === null ? 0 : value
    },
    totalBand(){
      return functions.formatTypicalPriceBand(this.totalValue)
    },
    totalDisplay(){
      return this.showPrices ? functions.formatUsd(this.totalValue, true) : this.totalBand
    },
    // Changes only when the build itself changes, so re-renders do not re-fire
    // the completion event.
    buildSignature(){
      return [
        this.source,
        this.buildId == null ? '' : this.buildId,
        this.rows.map((row) => (row.item ? row.type + ':' + (row.asin || row.item.id) + ':' + row.qty : '')).join('|')
      ].join('#')
    },
    analyticsPayload(){
      return {
        source: this.source,
        buildId: this.buildId,
        partsCount: this.plan.selectedCount,
        cartablePartsCount: this.plan.includedCount,
        missingAsinCount: this.plan.excludedCount,
        totalBand: this.totalBand,
        total: this.showPrices ? this.totalValue : null,
        conflicts: this.conflicts,
        warnings: this.warnings,
        performanceTier: this.tier.label
      }
    }
  },
  watch: {
    buildSignature: {
      immediate: true,
      handler(){
        this.reportCompleted()
      }
    }
  },
  methods: {
    friendlyCompatMessage(message){
      var match = String(message || '').match(/^Your (.+?) case is bigger than (.+?) motherboard\.?$/i)
      if(!match){ return message }
      return 'Compatible fit — the ' + match[2] + ' motherboard fits the ' + match[1] + ' case; some interior space will be unused.'
    },
    reportCompleted(){
      if(!this.reportCompletion || !this.rows.length || this.completedSignature === this.buildSignature){
        return
      }
      this.completedSignature = this.buildSignature
      try {
        analytics.trackBuildCompleted(Object.assign({}, this.analyticsPayload, {
          compatibility: this.compatValue,
          estimatedPower: this.estimatedPower,
          psuWattage: this.psuWattage
        }))
      } catch (e) { /* analytics must never block the buy flow */ }
    },
    onCartClick(){
      this.reportCartClick({
        mode: this.plan.mode,
        url: this.plan.url,
        asins: this.plan.entries.map((entry) => entry.asin),
        unitCount: this.plan.unitCount
      })
    },
    onStepClick(step, index){
      this.reportCartClick({
        mode: this.plan.mode,
        url: step.url,
        asins: [step.row.asin],
        unitCount: step.row.qty,
        step: index + 1
      })
    },
    reportCartClick(extra){
      try {
        window.dataLayer = window.dataLayer || []
        window.dataLayer.push(Object.assign({
          event: 'affiliate_cart_click',
          affiliate: 'amazon'
        }, this.analyticsPayload, extra))
      } catch (e) { /* never block navigation */ }
      try {
        analytics.trackBuildCartClick(Object.assign({}, this.analyticsPayload, extra))
      } catch (e) { /* best-effort */ }
    },
    onPartClick(row){
      try {
        analytics.trackAmazonClick({
          targetId: row.item && row.item.id,
          asin: row.asin,
          url: row.amazonUrl,
          partType: row.item && row.item.type,
          model: row.item && row.item.model,
          price: this.showPrices ? row.item && row.item.amzn_price : null,
          source: this.source + '_parts_list'
        })
      } catch (e) { /* best-effort */ }
    }
  }
}
</script>

<style scoped>
.buy-build {
  box-sizing: border-box;
  width: 100%;
  padding: 1.15rem 1.25rem 1.05rem;
  border: 1px solid rgba(37, 164, 207, 0.4);
  border-radius: 10px;
  background:
    radial-gradient(circle at 82% -20%, rgba(25, 151, 198, 0.14), transparent 42%),
    linear-gradient(160deg, rgba(8, 27, 37, 0.6), rgba(6, 11, 18, 0.78));
  color: #cfd2da;
}

.buy-build__head {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 1rem;
}

.buy-build__kicker {
  color: #25afe1;
  font-size: 10px;
  font-weight: 700;
  letter-spacing: 0.08em;
  text-transform: uppercase;
}

.buy-build__head h3 {
  margin: 0.3rem 0 0;
  color: #e6ebf2;
  font-size: 19px;
  font-weight: 650;
  line-height: 1.2;
}

.buy-build__count {
  flex: 0 0 auto;
  padding: 0.22rem 0.55rem;
  border: 1px solid rgba(54, 174, 216, 0.28);
  border-radius: 999px;
  background: rgba(25, 151, 198, 0.08);
  color: #99a9bd;
  font-size: 10px;
  font-weight: 700;
  letter-spacing: 0.04em;
  text-transform: uppercase;
}

.buy-build__metrics {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 0;
  margin-top: 1rem;
  overflow: hidden;
  border: 1px solid rgba(70, 82, 105, 0.58);
  border-radius: 8px;
  background: rgba(5, 9, 15, 0.32);
}

/* Must follow the base rule: equal specificity, so source order decides. Placed
   above it, the four-column default silently won and the fifth metric wrapped
   onto its own row. */
.buy-build__metrics--five {
  grid-template-columns: repeat(5, minmax(0, 1fr));
}

.buy-build__metric {
  min-width: 0;
  padding: 0.7rem 0.8rem;
  border-right: 1px solid rgba(70, 82, 105, 0.44);
}

.buy-build__metric:last-child {
  border-right: 0;
}

.buy-build__metric span {
  display: block;
  margin-bottom: 0.3rem;
  color: #8490a4;
  font-size: 10px;
  font-weight: 700;
  letter-spacing: 0.07em;
  text-transform: uppercase;
}

.buy-build__metric strong {
  display: block;
  overflow: hidden;
  color: #e2e7ef;
  font-size: 15px;
  font-weight: 700;
  line-height: 1.2;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.buy-build__metric--good strong { color: #32d3a9; }
.buy-build__metric--warning strong { color: #e4d836; }
.buy-build__metric--danger strong { color: #f06b79; }

.score-high, .score-good { color: #32d3a9; }
.score-mid { color: #e4d836; }
.score-low { color: #9ba6b9; }

.buy-build__alert {
  display: flex;
  gap: 0.65rem;
  margin-top: 0.9rem;
  padding: 0.7rem 0.85rem;
  border: 1px solid rgba(228, 216, 54, 0.42);
  border-radius: 8px;
  background: rgba(228, 216, 54, 0.07);
  color: #d8d2a0;
  font-size: 12px;
  line-height: 1.45;
}

.buy-build__alert--danger {
  border-color: rgba(230, 71, 89, 0.5);
  background: rgba(230, 71, 89, 0.08);
  color: #f0b6bc;
}

.buy-build__alert p,
.buy-build__alert ul {
  margin: 0;
}

.buy-build__alert ul {
  padding-left: 1.05rem;
}

.buy-build__alert-body {
  display: grid;
  min-width: 0;
  gap: 0.55rem;
}

.buy-build__finding-mark {
  margin-right: 0.4rem;
  opacity: 0.7;
}

.buy-build__finding-parts {
  display: flex;
  flex-wrap: wrap;
  gap: 0.35rem;
  margin-top: 0.3rem !important;
}

/* The two parts the finding is about, each a way into the part it names. */
.buy-build__finding-part {
  display: inline-flex;
  align-items: baseline;
  gap: 0.35rem;
  max-width: 100%;
  padding: 0.16rem 0.45rem;
  border: 1px solid currentColor;
  border-radius: 5px;
  color: inherit;
  font-size: 11px;
  line-height: 1.3;
  opacity: 0.78;
  text-decoration: none;
  transition: opacity 0.16s ease, background 0.16s ease;
}

.buy-build__finding-part:hover,
.buy-build__finding-part:focus {
  background: rgba(255, 255, 255, 0.06);
  color: inherit;
  opacity: 1;
  outline: none;
  text-decoration: none;
}

.buy-build__finding-part-kind {
  flex: none;
  font-size: 9px;
  font-weight: 800;
  letter-spacing: 0.07em;
  opacity: 0.72;
  text-transform: uppercase;
}

.buy-build__finding-part-name {
  overflow: hidden;
  font-weight: 650;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.buy-build__cta {
  margin-top: 1rem;
}

.buy-build a.buy-build__primary {
  display: flex !important;
  align-items: center;
  justify-content: center;
  gap: 0.55rem;
  width: 100%;
  min-height: 46px;
  padding: 0 1rem;
  border-color: #27bce9;
  border-radius: 7px;
  background: linear-gradient(180deg, #25b8e7, #1598c7);
  color: #061019;
  font-size: 13.5px;
  font-weight: 800;
  letter-spacing: 0.02em;
  text-decoration: none;
  text-transform: uppercase;
  box-shadow: 0 10px 24px rgba(0, 145, 198, 0.18);
}

.buy-build a.buy-build__primary:hover,
.buy-build a.buy-build__primary:focus {
  border-color: #4bc9f3;
  background: #27b4e4;
  color: #02070b;
  text-decoration: none;
}

.buy-build__cta-note {
  margin: 0.55rem 0 0;
  color: #a1abbd;
  font-size: 11.5px;
  line-height: 1.45;
}

.buy-build__cta-note--empty {
  color: #b6a0a4;
}

.buy-build__steps ol {
  margin: 0.4rem 0 0;
  padding-left: 1.25rem;
  color: #cfd2da;
  font-size: 12.5px;
  line-height: 1.7;
}

.buy-build__steps a {
  color: #45c5f2;
}

.buy-build__steps span {
  margin-left: 0.35rem;
  color: #8b95a8;
}

/* Sits above the CTA: it changes how many parts the button actually adds. */
.buy-build__excluded {
  display: flex;
  align-items: flex-start;
  gap: 0.45rem;
  margin: 0 0 0.55rem;
  color: #c9b98f;
  font-size: 11.5px;
  line-height: 1.45;
}

.buy-build__secondary {
  margin-top: 0.7rem;
}

/* The parts list is the only part of this panel that grows with the build, so it
   is the part that scrolls. Without this the completed-build screen ran far
   taller than the slider/list it replaces and pushed the CTA off-screen. */
.buy-build__parts {
  margin-top: 1.05rem;
  max-height: min(44vh, 400px);
  overflow-y: auto;
  overflow-x: hidden;
  border: 1px solid rgba(70, 82, 105, 0.58);
  border-radius: 8px;
  scrollbar-width: thin;
}

.buy-build__parts::-webkit-scrollbar {
  width: 8px;
}

.buy-build__parts::-webkit-scrollbar-thumb {
  border-radius: 8px;
  background: rgba(102, 112, 132, 0.55);
}

/* Column labels stay put while the rows move under them. */
.buy-build__parts-head {
  position: sticky;
  top: 0;
  z-index: 1;
}

.buy-build__parts-head,
.buy-build__row {
  display: grid;
  grid-template-columns: minmax(120px, 0.85fr) minmax(0, 2fr) minmax(90px, 0.8fr) minmax(64px, 0.5fr);
  gap: 0.75rem;
  align-items: center;
  padding: 0.55rem 0.85rem;
}

.buy-build__parts-head {
  /* Opaque, not translucent: it is sticky now and rows scroll beneath it. */
  background: #0b1018;
  color: #8490a4;
  font-size: 10px;
  font-weight: 700;
  letter-spacing: 0.07em;
  text-transform: uppercase;
}

.buy-build__row {
  border-top: 1px solid rgba(70, 82, 105, 0.34);
  font-size: 12.5px;
}

.buy-build__row.is-missing,
.buy-build__row.is-excluded,
.buy-build__row.is-unavailable {
  background: rgba(228, 216, 54, 0.04);
}

.buy-build__row-label {
  display: flex;
  align-items: center;
  gap: 0.4rem;
  min-width: 0;
  color: #9ba6b9;
}

.buy-build__qty {
  color: #32d3a9;
}

.buy-build__row-name {
  min-width: 0;
}

.buy-build__row-name strong {
  display: block;
  overflow: hidden;
  color: #e2e7ef;
  font-weight: 600;
  text-overflow: ellipsis;
  white-space: nowrap;
}

.buy-build__row-name span {
  display: block;
  color: #8b95a8;
  font-size: 11px;
}

.buy-build__row-flag {
  color: #d8c98f !important;
}

/* Prices are yellow everywhere in the builder. */
.buy-build__total,
.buy-build__row-price strong,
.buy-build__row-price .buy-build__row-band {
  color: #e4d836;
  font-variant-numeric: tabular-nums;
}

.buy-build__row-price .buy-build__row-band {
  font-size: 12.5px;
  font-weight: 700;
  white-space: nowrap;
}

.buy-build__row-price span {
  display: block;
  color: #8b95a8;
  font-size: 11px;
}

.buy-build__row-link a {
  color: #45c5f2;
}

.buy-build__row-link span {
  color: #6e7788;
}

.buy-build__disclosure {
  margin: 0.75rem 0 0;
  color: #8793a7;
  font-size: 10.5px;
  line-height: 1.45;
}

@media (max-width: 1100px) {
  .buy-build__metrics--five {
    grid-template-columns: repeat(3, minmax(0, 1fr));
  }
}

@media (max-width: 900px) {
  .buy-build__metrics,
  .buy-build__metrics--five {
    grid-template-columns: repeat(2, minmax(0, 1fr));
  }

  .buy-build__metric:nth-child(2) {
    border-right: 0;
  }

  .buy-build__metric:nth-child(-n + 2) {
    border-bottom: 1px solid rgba(70, 82, 105, 0.44);
  }
}

@media (max-width: 640px) {
  .buy-build {
    padding: 1rem 0.9rem;
  }

  .buy-build__parts-head {
    display: none;
  }

  .buy-build__row {
    grid-template-columns: minmax(0, 1fr) minmax(74px, auto);
    grid-template-areas:
      "label link"
      "name  name"
      "price price";
    row-gap: 0.25rem;
  }

  .buy-build__row-label { grid-area: label; }
  .buy-build__row-name { grid-area: name; }
  .buy-build__row-price { grid-area: price; }
  .buy-build__row-link { grid-area: link; text-align: right; }
}
.buy-build__estimate { display: grid; gap: 5px; flex-shrink: 0; text-align: right; }
.buy-build__estimate > span { color: #9198aa; font-size: 11px; }
.buy-build__estimate > strong { color: #e4d836; font-size: 22px; line-height: 1.25; }
.buy-build__compat-summary { margin: 16px 0 0; color: #8fe8c4; font-size: 12px; }
.buy-build__compat-summary.tone-warning { color: #e0c07e; }
.buy-build__compat-summary.tone-danger { color: #f06b79; }
.buy-build--compact .buy-build__analysis { margin-top: 18px; padding-top: 12px; border-top: 1px solid rgba(67,72,87,0.5); }
.buy-build__analysis > summary { width: fit-content; color: #a4acbc; font-size: 12px; cursor: pointer; }
.buy-build__analysis > summary:hover { color: #7fd0ee; }
.buy-build__analysis > summary:focus-visible, .buy-build__details-toggle:focus-visible { outline: 2px solid #7fd0ee; outline-offset: 4px; }
.buy-build__metrics.buy-build__metrics--compact { grid-template-columns: repeat(3, minmax(0, 1fr)); }
.buy-build__metrics--compact strong { white-space: normal; line-height: 1.4; }
.buy-build__details-toggle { display: inline-flex; align-items: center; gap: 8px; margin-top: 7px; padding: 0; border: 0; background: transparent; color: inherit; font: inherit; font-size: 11px; cursor: pointer; text-decoration: underline; text-underline-offset: 3px; }
.buy-build__alert.buy-build__alert--fit-note { width: fit-content; max-width: 100%; padding: 0; border: 0; border-radius: 0; background: transparent; color: #a4acbc; }
.buy-build__alert--fit-note > .icon { flex-shrink: 0; color: #e0c07e; }
.buy-build__alert--fit-note .buy-build__alert-body { display: flex; flex-wrap: wrap; align-items: baseline; gap: 8px 14px; }
.buy-build__alert--fit-note .buy-build__finding { min-width: 0; max-width: 100%; }
.buy-build__alert--fit-note .buy-build__details-toggle { margin-top: 0; color: #7fd0ee; white-space: nowrap; }
.buy-build--compact .buy-build__cta { display: grid; grid-template-columns: minmax(0, 1fr) auto; align-items: center; gap: 10px 18px; margin-top: 20px; }
.buy-build--compact .buy-build__excluded, .buy-build--compact .buy-build__steps, .buy-build--compact .buy-build__cta-note { grid-column: 1 / -1; margin: 0; }
.buy-build--compact .buy-build__excluded { grid-row: 1; }
.buy-build--compact .buy-build__cta-note--handoff { grid-row: 3; }
.buy-build--compact a.buy-build__primary { grid-row: 2; width: fit-content; max-width: 100%; margin: 0; font-size: 12px; text-transform: none; box-shadow: none; }
.buy-build--compact .buy-build__secondary { grid-column: 2; grid-row: 2; margin: 0; }
.buy-build--compact .buy-build__disclosure { margin-top: 16px; font-size: 10px; color: #8f98aa; }
@media (max-width: 600px) {
  .buy-build--compact .buy-build__head { flex-direction: column; }
  .buy-build__estimate { text-align: left; }
  .buy-build--compact .buy-build__cta { grid-template-columns: minmax(0, 1fr); }
  .buy-build--compact a.buy-build__primary { width: 100%; }
  .buy-build--compact .buy-build__secondary { grid-column: 1; grid-row: auto; }
  .buy-build--compact .buy-build__cta--bulk .buy-build__secondary { grid-row: 4; }
  .buy-build__metrics.buy-build__metrics--compact { grid-template-columns: 1fr; }
  .buy-build__metrics--compact .buy-build__metric { border-right: 0; border-bottom: 1px solid rgba(67,72,87,0.4); }
}
</style>
